import React from 'react';
import { Drawer, Box, IconButton, Typography, Chip, Divider, Button } from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import SoapNoteCard from '../../../components/SoapNote/SoapNoteCard';
import MedicationCard from '../../../components/Medication/MedicationCard';
import ProcedureCard from '../../../components/Procedure/ProcedureCard';
import VaccinationCard from '../../../components/Vaccination/VaccinationCard';
import { SoapNote } from '../../../types/soap';
import ShareSentDetail from '../../../components/StableDocs/ShareSentDetail';
import { TimelineEvent, kindMeta, safeDate } from './TimelineRow';

export interface EventDetailDrawerProps {
  event: TimelineEvent | null;
  onClose: () => void;
  // Per-kind action wiring — passed straight through to the embedded cards
  // so existing edit/menu behavior keeps working inside the drawer.
  horseId?: string;
  canEdit?: boolean;
  canArchive?: boolean;
  onSoapView?: (note: SoapNote) => void;
  onSoapEdit?: (note: SoapNote) => void;
  onSoapMenu?: (event: React.MouseEvent<HTMLElement>, noteId: string) => void;
  onProcedureEdit?: (proc: any) => void;
  onProcedureMenu?: (event: React.MouseEvent<HTMLElement>, procId: string) => void;
  onVaccinationMenu?: (event: React.MouseEvent<HTMLElement>, vaxId: string) => void;
  /** Present only for someone who may edit the record (PUT /vaccinations/:id). */
  onVaccinationEdit?: (vaccination: any) => void;
  onRecordMenu?: (event: React.MouseEvent<HTMLButtonElement>, recordId: string) => void;
  onRecordEdit?: (record: any) => void;
  /** Open the discharge dialog on a specific summary (timeline → draft view/edit). */
  onDischargeOpen?: (summaryId: string) => void;
  lowStockItems?: string[];
  totalActiveMedications?: number;
  recordAttachmentMap?: Record<string, any>;
}

const DRAWER_WIDTH = 540;

/**
 * Right-side drawer that shows the full detail for the timeline row the user
 * clicked. We render the existing per-type card inside so we get the existing
 * expand/collapse + action menus without duplicating render logic.
 */
const EventDetailDrawer: React.FC<EventDetailDrawerProps> = ({
  event,
  onClose,
  horseId,
  canEdit,
  canArchive,
  onSoapView,
  onSoapEdit,
  onSoapMenu,
  onProcedureEdit,
  onProcedureMenu,
  onVaccinationMenu,
  onVaccinationEdit,
  onRecordMenu,
  onRecordEdit,
  onDischargeOpen,
  lowStockItems = [],
  totalActiveMedications = 0,
  recordAttachmentMap = {},
}) => {
  const open = !!event;
  const meta = event ? kindMeta(event.kind) : null;

  const renderBody = () => {
    if (!event) return null;
    const item = event.raw;

    if (event.kind === 'soap') {
      return (
        <SoapNoteCard
          soapNote={item}
          onView={onSoapView}
          onEdit={onSoapEdit}
          onMenu={canArchive ? onSoapMenu : undefined}
          canEdit={canEdit}
        />
      );
    }

    if (event.kind === 'medication') {
      const meds = item.itemType === 'medication-group' ? item.medications : [item];
      return (
        <MedicationCard
          medications={meds}
          date={item.date || item.startDate}
          lowStockItems={lowStockItems}
          totalActiveMedications={totalActiveMedications}
          horseId={horseId}
        />
      );
    }

    if (event.kind === 'procedure') {
      return (
        <ProcedureCard
          record={item}
          onEdit={onProcedureEdit}
          onMenu={canArchive ? onProcedureMenu : undefined}
          canEdit={canEdit}
        />
      );
    }

    if (event.kind === 'vaccination') {
      return (
        <VaccinationCard
          record={item}
          lowStockItems={lowStockItems}
          onMenu={canArchive ? onVaccinationMenu : undefined}
          attachment={recordAttachmentMap[item.id]}
          onEdit={onVaccinationEdit}
          canEdit={!!onVaccinationEdit}
        />
      );
    }

    if (event.kind === 'stabledocs') return <ShareSentDetail share={item} />;

    if (event.kind === 'discharge') {
      // Discharge summaries have no `notes` body, so the generic fallback below
      // would render an empty card. Show the draft/finalized status and a button
      // that opens the discharge dialog directly on this summary.
      const status = item.status || 'DRAFT';
      const isFinalized = status === 'FINALIZED';
      return (
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
            <Chip
              label={isFinalized ? 'Finalized' : 'Draft'}
              color={isFinalized ? 'success' : 'default'}
              size="small"
            />
            {item.attendingVet && (
              <Typography variant="caption" color="text.secondary">
                Attending vet: {item.attendingVet}
              </Typography>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isFinalized
              ? 'This discharge summary has been finalized.'
              : 'This discharge summary is still a draft.'}
          </Typography>
          {onDischargeOpen && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => onDischargeOpen(item.id)}
            >
              {isFinalized ? 'View discharge summary' : 'View / edit draft'}
            </Button>
          )}
        </Box>
      );
    }

    // Visit notes + raw medical records — render inline since RecordCard is
    // private to HorseProfile. Keeps the drawer self-sufficient.
    const isImported = typeof item.notes === 'string' && item.notes.startsWith('[Imported Document]');
    const importedTitle = isImported
      ? item.notes.split('\n')[0].replace('[Imported Document] ', '').trim()
      : null;
    const displayNotes = isImported ? importedTitle : item.notes;
    return (
      <Box>
        <Typography variant="caption" color="text.secondary">
          {(() => { const d = safeDate(item.date); return d ? format(d, 'MMMM d, yyyy h:mm a') : 'Unknown date'; })()}
        </Typography>
        {item.tags && item.tags.length > 0 && (
          <Box display="flex" gap={0.5} flexWrap="wrap" my={1}>
            {item.tags.map((tag: string, i: number) => (
              <Chip key={i} label={tag} size="small" variant="outlined" />
            ))}
          </Box>
        )}
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
          {displayNotes}
        </Typography>
        {canEdit && event.kind === 'visit_note' && onRecordEdit && (
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={() => onRecordEdit(item)}
            sx={{ mt: 2 }}
          >
            Edit Note
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: DRAWER_WIDTH },
          maxWidth: '100vw',
        },
      }}
      ModalProps={{ keepMounted: false }}
    >
      {event && meta && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Chip
                  icon={meta.icon}
                  label={meta.label}
                  size="small"
                  sx={{
                    bgcolor: meta.bg,
                    color: meta.color,
                    fontWeight: 600,
                    height: 22,
                    '& .MuiChip-icon': { color: meta.color },
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {(() => { const d = safeDate(event.date); return d ? format(d, 'MMM d, yyyy · h:mm a') : 'Unknown date'; })()}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                {event.title}
              </Typography>
              {event.by && (
                <Typography variant="caption" color="text.secondary">
                  by {event.by}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={onClose} aria-label="close detail">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>{renderBody()}</Box>
        </Box>
      )}
    </Drawer>
  );
};

export default EventDetailDrawer;
